import { useContext, useMemo, useState } from 'react'

import { cn, Text } from '@optechai/design-system'

import { useInterval } from '../../hooks/use-interval'

import { InvertAlignmentContext } from './context'

const DEFAULT_PROGRESSIVE_LOADING_MESSAGES = {
  0.0: 'Reading',
  0.125: 'Thinking',
  0.25: 'Gathering information',
  0.3756: 'Thinking',
  0.5: 'Drafting response',
  0.625: 'Thinking',
  0.75: 'Finalizing response',
  0.875: 'Thinking',
  1.0: 'Checking response',
} as const satisfies Record<number, string>

/**
 * A component that displays a progressive loading message.
 */
export const ProgressiveLoadingMessage = ({
  labels = DEFAULT_PROGRESSIVE_LOADING_MESSAGES,
  estimatedDurationMs,
}: {
  labels?: Record<number, string>
  estimatedDurationMs: number
}) => {
  const inverted = useContext(InvertAlignmentContext)
  const startedAt = useMemo(() => Date.now(), [])
  const [progress, setProgress] = useState(0)
  const [opacity, setOpacity] = useState(1)
  const sortedKeys = useMemo(
    () =>
      Object.keys(labels)
        .map(parseFloat)
        .sort((a, b) => b - a),
    [labels],
  )
  const label = useMemo(() => {
    const key = sortedKeys.find((key) => progress >= key)
    return key ? labels[key] : 'Thinking'
  }, [labels, progress, sortedKeys])

  useInterval(() => {
    const elapsed = Date.now() - startedAt
    const newProgress = Math.min(elapsed / estimatedDurationMs, 1)
    setProgress(newProgress)
    setOpacity((prev) => (prev === 1 ? 0.5 : 1))
  }, 1000)

  return (
    <div
      className={cn(
        'flex items-center gap-1',
        inverted ? 'flex-row-reverse' : 'flex-row',
      )}
    >
      <Text
        className="text-subtle transition-opacity duration-1000"
        style={{ opacity }}
        variant="p-xs"
      >
        {label}
      </Text>
    </div>
  )
}
